import React, { useState, useEffect } from 'react';
import {
    Container,
    Card,
    Alert,
    ProgressBar,
    Badge,
    Button,
    Modal
} from 'react-bootstrap';
import { getAuthenticatedAxios } from './axiosConfig';
import { useAuth } from '../AuthContext/AuthContext';
import { BASE_URL } from '../../Constants/constants';

// Import sub-components for each checkout stage
import StaffCheckoutInitiation from './StaffInitiation';
import ICTCheckoutStage from './ICTCheckoutStage';
import SecurityCheckoutStage from './SecurityCheckoutStage';
import HRCheckoutStage from './HRCheckoutStage';
import { useTranslation } from 'react-i18next';
import { formatDate, formatDateTimeFull } from './Dateutils';

const CheckoutProcess = () => {
    const { t } = useTranslation('checkoutProcess');
    const { currentUser } = useAuth();
    const [checkoutStatus, setCheckoutStatus] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showClearModal, setShowClearModal] = useState(false);
    const [isClearing, setIsClearing] = useState(false);

    // Function to fetch checkout status
    const fetchCheckoutStatus = async () => {
        try {
            setLoading(true);
            const authenticatedAxios = getAuthenticatedAxios();
            const response = await authenticatedAxios.get(`${BASE_URL}/checkout/status`);

            if (response.data) {
                setCheckoutStatus(response.data);
                setError(null);
            } else {
                setCheckoutStatus(null);
            }
        } catch (err) {
            console.error(t('errors.errorFetchingCheckoutStatus'), err);
            if (err.response?.status === 404 || !err.response?.data) {
                setCheckoutStatus(null);
                setError(null);
            } else {
                setError(t('alerts.error_message'));
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (currentUser) {
            fetchCheckoutStatus();
        }
    }, [currentUser]);

    const refreshStatus = () => {
        fetchCheckoutStatus();
    };

    const clearCheckout = async () => {
        try {
            setIsClearing(true);
            const authenticatedAxios = getAuthenticatedAxios();
            await authenticatedAxios.post(`${BASE_URL}/checkout/clear/${currentUser.id}`);
            setShowClearModal(false);
            setCheckoutStatus(null);
            fetchCheckoutStatus();
        } catch (err) {
            setShowClearModal(false);
            setError(err.response?.data?.detail || 'Failed to clear checkout.');
        } finally {
            setIsClearing(false);
        }
    };

    const calculateProgress = () => {
        if (!checkoutStatus) return 0;
        let progress = 0;

        // Add progress for user confirmation
        if (checkoutStatus.user_return_confirmed) progress += 25;

        // Add progress for department checkouts
        if (checkoutStatus.ict_checkout_status) progress += 25;
        if (checkoutStatus.security_checkout_status) progress += 25;
        if (checkoutStatus.hr_checkout_status) progress += 25;

        return progress;
    };


    const getStatusBadge = (status, label, notes, userConfirmed = false, isICT = false) => {
        let variant = 'warning';
        let text = t('status_badges.pending');

        if (status === true) {
            variant = 'success';
            text = t('status_badges.completed');
        } else if (isICT && userConfirmed) {
            variant = 'info';
            text = t('labels.awaitingVerification');
        } else if (status === false && notes && !userConfirmed) {
            variant = 'danger';
            text = t('status_badges.rejected');
        }

        return <Badge bg={variant}>{label}: {text}</Badge>;
    };

    const renderTimeline = () => {
        if (!checkoutStatus) return null;

        const timelineEvents = [];

        // Add checkout initiation
        if (checkoutStatus.created_at) {
            timelineEvents.push({
                date: checkoutStatus.created_at,
                event: t('events.checkoutProcessInitiated'),
                type: 'info',
                icon: 'bi-play-circle'
            });
        }

        // Add user return confirmation
        if (checkoutStatus.user_return_date) {
            timelineEvents.push({
                date: checkoutStatus.user_return_date,
                event: t('events.equipmentReturnConfirmedByUser'),
                type: 'info',
                icon: 'bi-check-circle'
            });
        }

        // Add ICT events
        if (checkoutStatus.ict_checkout_status === false && checkoutStatus.ict_checkout_notes) {
            timelineEvents.push({
                date: new Date().toISOString(),
                event: `${t('timeline.ict_rejected')} ${checkoutStatus.ict_checkout_notes}`,
                type: 'danger',
                icon: 'bi-x-circle'
            });
        } else if (checkoutStatus.ict_checkout_date && checkoutStatus.ict_checkout_status) {
            const eventText = checkoutStatus.ict_checked_by
                ? `${t('timeline.ict_completed')} by ${checkoutStatus.ict_checked_by}`
                : t('timeline.ict_completed');
            timelineEvents.push({
                date: checkoutStatus.ict_checkout_date,
                event: eventText,
                type: 'success',
                icon: 'bi-laptop'
            });
        }

        // Add Security events
        if (checkoutStatus.security_checkout_date && checkoutStatus.security_checkout_status) {
            const eventText = checkoutStatus.security_checked_by
                ? `${t('timeline.security_completed')} by ${checkoutStatus.security_checked_by}`
                : t('timeline.security_completed');
            timelineEvents.push({
                date: checkoutStatus.security_checkout_date,
                event: eventText,
                type: 'success',
                icon: 'bi-shield-check'
            });
        }

        // Add HR events
        if (checkoutStatus.hr_checkout_date && checkoutStatus.hr_checkout_status) {
            const eventText = checkoutStatus.hr_checked_by
                ? `${t('timeline.hr_completed')} by ${checkoutStatus.hr_checked_by}`
                : t('timeline.hr_completed');
            timelineEvents.push({
                date: checkoutStatus.hr_checkout_date,
                event: eventText,
                type: 'success',
                icon: 'bi-person-check'
            });
        }

        // Add completion event
        if (checkoutStatus.is_checkout_complete && checkoutStatus.checkout_completion_date) {
            timelineEvents.push({
                date: checkoutStatus.checkout_completion_date,
                event: t('events.checkoutProcessCompleted'),
                type: 'success',
                icon: 'bi-check-circle-fill'
            });
        }

        // Sort events by date
        timelineEvents.sort((a, b) => new Date(a.date) - new Date(b.date));

        if (timelineEvents.length === 0) return null;

        return (
            <Card className="mt-4">
                <Card.Header>
                    <h5 className="mb-0">
                        <i className="bi bi-clock-history me-2"></i>
                        {t('headings.timeline')}
                    </h5>
                </Card.Header>
                <Card.Body>
                    <div className="timeline">
                        {timelineEvents.map((event, index) => (
                            <div key={index} className={`timeline-item ${event.type}`}>
                                <div className="timeline-marker">
                                    <i className={`bi ${event.icon}`}></i>
                                </div>
                                <div className="timeline-content">
                                    <div className="timeline-header">
                                        <strong>{event.event}</strong>
                                    </div>
                                    <div className="timeline-time text-muted">
                                        <i className="bi bi-calendar3 me-1"></i>
                                        {formatDate(event.date)}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </Card.Body>
            </Card>
        );
    };

    const renderCurrentStage = () => {
        if (checkoutStatus.is_checkout_complete) {
            return (
                <Alert variant="success">
                    <Alert.Heading>
                        <i className="bi bi-check-circle-fill me-2"></i>
                        {t('headings.completed')}
                    </Alert.Heading>
                    <p>{t('alerts.success_message')}</p>
                    <hr />
                    <p className="mb-0">
                        <strong>{t('alerts.completion_date', { date: '' })}</strong> {formatDate(checkoutStatus.checkout_completion_date)}
                    </p>
                </Alert>
            );
        }

        // ICT Stage — show when:
        // 1. Staff hasn't confirmed equipment return yet
        // 2. After rejection — ict_checkout_status is false (old backend) or null (new backend)
        //    In both cases the staff needs to see the rejection and resubmit
        // FIXED: pass refreshStatus as onCheckoutUpdate so the parent always
        // re-fetches the full status from the server after any child update.
        const ictRejected = checkoutStatus.ict_checkout_status === false ||
            checkoutStatus.ict_checkout_status === null;
        const needsICTAction = !checkoutStatus.user_return_confirmed ||
            (ictRejected && checkoutStatus.ict_checkout_status !== true);

        if (needsICTAction) {
            return <ICTCheckoutStage checkoutStatus={checkoutStatus} onCheckoutUpdate={refreshStatus} />;
        }

        // Security Stage - After ICT checkout is complete
        if (checkoutStatus.ict_checkout_status === true &&
            !checkoutStatus.security_checkout_status) {
            return <SecurityCheckoutStage checkoutStatus={checkoutStatus} onStatusChange={setCheckoutStatus} />;
        }

        // HR Stage - Final stage after security
        if (checkoutStatus.ict_checkout_status === true &&
            checkoutStatus.security_checkout_status === true &&
            !checkoutStatus.hr_checkout_status) {
            return <HRCheckoutStage checkoutStatus={checkoutStatus} onCheckoutUpdate={setCheckoutStatus} />;
        }

        // Show HR in progress alert if security is complete
        if (checkoutStatus.ict_checkout_status &&
            checkoutStatus.security_checkout_status &&
            !checkoutStatus.hr_checkout_status) {
            return (
                <Alert variant="info">
                    <Alert.Heading>
                        <i className="bi bi-hourglass-split me-2"></i>
                        {t('stages.hr_in_progress')}
                    </Alert.Heading>
                    <p>{t('stages.security_complete_note')}</p>
                    <ul>
                        <li>{t('stages.exit_interview')}</li>
                        <li>{t('stages.final_documentation')}</li>
                        <li>{t('stages.clearance_verification')}</li>
                    </ul>
                    {checkoutStatus.security_checkout_date && (
                        <div className="mt-3">
                            <Badge bg="success">
                                {t('stages.security_completed_label')} {formatDate(checkoutStatus.security_checkout_date)}
                            </Badge>
                        </div>
                    )}
                </Alert>
            );
        }

        return null;
    };

    const renderCheckoutStage = () => {
        if (loading) {
            return (
                <Alert variant="info">
                    <div className="d-flex align-items-center">
                        <div className="spinner-border spinner-border-sm me-2" role="status">
                            <span className="visually-hidden">{t('labels.loading')}</span>
                        </div>
                        {t('progress.loading')}
                    </div>
                </Alert>
            );
        }

        if (!checkoutStatus) {
            return (
                <div className="text-center">
                    <Alert variant="info" className="mb-4">
                        {t('alerts.welcome_message')} {t('buttons.start_process')}
                    </Alert>
                    <StaffCheckoutInitiation
                        onCheckoutInitiated={(status) => {
                            setCheckoutStatus(status);
                            refreshStatus();
                        }}
                    />
                </div>
            );
        }

        return (
            <>
                <Card className="mb-4">
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <ProgressBar
                                now={calculateProgress()}
                                className="flex-grow-1 me-3"
                            />
                            <div className="d-flex gap-2">
                                <button
                                    className="btn btn-outline-primary btn-sm"
                                    onClick={refreshStatus}
                                    disabled={loading}
                                >
                                    {loading ? t('buttons.refreshing') : t('buttons.refresh_status')}
                                </button>
                                {/* Clear Checkout — available to any staff as long as HR has not finalized */}
                                {checkoutStatus &&
                                    !checkoutStatus.is_checkout_complete &&
                                    !checkoutStatus.hr_checkout_status && (
                                        <button
                                            className="btn btn-outline-danger btn-sm"
                                            onClick={() => setShowClearModal(true)}
                                            disabled={loading}
                                            title="Clear checkout and restart from the beginning"
                                        >
                                            Clear Checkout
                                        </button>
                                    )}
                            </div>
                        </div>

                        <div className="d-flex gap-2 flex-wrap">
                            {checkoutStatus.user_return_confirmed && (
                                <Badge bg="info">
                                    {t('status_badges.equipment_return')}: {t('status_badges.confirmed')}
                                    {checkoutStatus.user_return_date && (
                                        <div className="small">
                                            {formatDate(checkoutStatus.user_return_date)}
                                        </div>
                                    )}
                                </Badge>
                            )}
                            {getStatusBadge(
                                checkoutStatus.ict_checkout_status,
                                'ICT',
                                checkoutStatus.ict_checkout_notes,
                                checkoutStatus.user_return_confirmed,
                                true
                            )}
                            {getStatusBadge(checkoutStatus.security_checkout_status, t('labels.security'))}
                            {getStatusBadge(checkoutStatus.hr_checkout_status, 'HR')}
                        </div>

                        {/* Show completion dates */}
                        <div className="mt-3">
                            <small className="text-muted">
                                {checkoutStatus.created_at && (
                                    <div>{t('dates.started')} {formatDate(checkoutStatus.created_at)}</div>
                                )}
                                {checkoutStatus.ict_checkout_date && checkoutStatus.ict_checkout_status && (
                                    <div>{t('dates.ict')} {formatDate(checkoutStatus.ict_checkout_date)}</div>
                                )}
                                {checkoutStatus.security_checkout_date && checkoutStatus.security_checkout_status && (
                                    <div>{t('dates.security')} {formatDate(checkoutStatus.security_checkout_date)}</div>
                                )}
                                {checkoutStatus.hr_checkout_date && checkoutStatus.hr_checkout_status && (
                                    <div>{t('dates.hr')} {formatDate(checkoutStatus.hr_checkout_date)}</div>
                                )}
                            </small>
                        </div>
                    </Card.Body>
                </Card>

                {renderCurrentStage()}
                {renderTimeline()}
            </>
        );
    };

    return (
        <Container className="mt-4">
            <Card>
                <Card.Header>
                    <h2>{t('title')}</h2>
                </Card.Header>
                <Card.Body>
                    {error && (
                        <Alert variant="danger" dismissible onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}
                    {renderCheckoutStage()}
                </Card.Body>
            </Card>

            {/* Custom CSS for timeline */}
            <style jsx>{`
                .timeline {
                    position: relative;
                    padding-left: 30px;
                }

                .timeline::before {
                    content: '';
                    position: absolute;
                    left: 15px;
                    top: 0;
                    bottom: 0;
                    width: 2px;
                    background: #dee2e6;
                }

                .timeline-item {
                    position: relative;
                    margin-bottom: 20px;
                    padding-left: 25px;
                }

                .timeline-marker {
                    position: absolute;
                    left: -23px;
                    top: 5px;
                    width: 30px;
                    height: 30px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 14px;
                    color: white;
                }

                .timeline-item.info .timeline-marker {
                    background-color: #0dcaf0;
                }

                .timeline-item.success .timeline-marker {
                    background-color: #198754;
                }

                .timeline-item.danger .timeline-marker {
                    background-color: #dc3545;
                }

                .timeline-content {
                    background: #f8f9fa;
                    border: 1px solid #dee2e6;
                    border-radius: 5px;
                    padding: 12px;
                }

                .timeline-header {
                    font-weight: 600;
                    margin-bottom: 5px;
                }

                .timeline-time {
                    font-size: 0.875rem;
                }
            `}</style>
            {/* Clear Checkout Confirmation Modal */}
            <Modal
                show={showClearModal}
                onHide={() => !isClearing && setShowClearModal(false)}
                centered
                backdrop={isClearing ? 'static' : true}
                keyboard={!isClearing}
            >
                <Modal.Header closeButton={!isClearing}>
                    <Modal.Title className="text-danger">
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        Clear Checkout Process
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {isClearing ? (
                        <div className="text-center py-3">
                            <div className="spinner-border text-danger mb-3" role="status">
                                <span className="visually-hidden">Clearing...</span>
                            </div>
                            <p className="mb-0 text-muted">Clearing checkout, please wait...</p>
                        </div>
                    ) : (
                        <>
                            <p>Are you sure you want to clear your checkout process?</p>
                            <Alert variant="warning" className="mb-0">
                                <ul className="mb-0">
                                    <li>All completed stages will be reset</li>
                                    <li>Equipment return confirmation will be cleared</li>
                                    <li>You will need to restart from the beginning</li>
                                </ul>
                            </Alert>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowClearModal(false)} disabled={isClearing}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={clearCheckout} disabled={isClearing}>
                        {isClearing ? (
                            <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Clearing...</>
                        ) : 'Yes, Clear Checkout'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default CheckoutProcess;